/**
 * The failure vocabulary, shared by both ends.
 *
 * A code is a promise about what the other side should do next, which is why
 * every one of them carries a retry policy rather than leaving the client to
 * guess from the wording. Guessing is how a client ends up reconnecting in a
 * tight loop against a server that has already said the token is wrong.
 */

export const ERROR_CODES = [
  /** The client asked for a protocol version this server does not speak. */
  'protocol_unsupported',
  /** The frame did not parse, or failed validation. */
  'bad_message',
  /** A message arrived that makes no sense for the current session state. */
  'bad_state',
  /** No credentials, or credentials that do not belong to anybody. */
  'unauthorized',
  /** Good credentials that have expired. Distinct because the fix differs. */
  'token_expired',
  /** The device or account has been shut off. */
  'revoked',
  /**
   * Authenticated fine, and not allowed to do this.
   *
   * Distinct from `unauthorized` because the remedy is different and a client
   * that confuses them misbehaves: `unauthorized` says fetch a fresh token,
   * and a client that does that on a forbidden spins through the token
   * endpoint forever without ever becoming any more permitted than it was.
   *
   * Reserved for the case where the caller is *meant* to know the thing exists
   * and simply may not act on it: a field agent trying to publish knowledge,
   * a viewer trying to open an individual record. Reaching across
   * organizations is `not_found` instead, because confirming an id exists is
   * itself a small leak.
   */
  'forbidden',
  /** Asked to resume a session that is gone or belongs to somebody else. */
  'session_not_found',
  /**
   * The thing asked for is not there, or the caller may not be told that it is.
   *
   * The generic 404 for every resource that is not a conversation. It exists
   * because `session_not_found` was carrying both jobs, and a platform with
   * twenty resource types cannot tell a client "that conversation is no longer
   * available" when what it asked for was a case, an indicator or a staff
   * user.
   */
  'not_found',
  /** Too many messages, connections or sessions, too quickly. */
  'rate_limited',
  /** The account has used up its allowance. */
  'quota_exceeded',
  /** The session hit the wall-clock ceiling. */
  'session_expired',
  /** The client offered an audio format the server cannot take. */
  'audio_unsupported',
  /** The client sent audio faster than it could be forwarded. */
  'audio_overflow',
  /** No voice model is reachable right now. */
  'model_unavailable',
  /** The voice model was reachable and failed. */
  'model_error',
  /** The server is shutting down. Reconnect, somewhere else if need be. */
  'going_away',
  /** Anything unclassified. Always a bug in this server. */
  'internal',
] as const

export type ErrorCode = (typeof ERROR_CODES)[number]

/**
 * Whether a client should try the same thing again.
 *
 * `retry` means the request was fine and the server was not: back off and
 * repeat it. `reauth` means get fresh credentials first. `fatal` means the
 * client is wrong and repeating it will fail again forever.
 */
export type Recovery = 'retry' | 'reauth' | 'fatal'

const RECOVERY: Record<ErrorCode, Recovery> = {
  protocol_unsupported: 'fatal',
  bad_message: 'fatal',
  bad_state: 'fatal',
  unauthorized: 'reauth',
  token_expired: 'reauth',
  revoked: 'fatal',
  // Retrying with the same credential cannot help, and neither can a fresh
  // one: the permission belongs to the role, not to the token.
  forbidden: 'fatal',
  session_not_found: 'retry',
  /*
   * Fatal where `session_not_found` is retryable, and the difference is real
   * rather than an oversight. A lost conversation can be replaced by starting
   * a new one, so a client that retries gets somewhere. A case that is not
   * there will not be there on the second request either.
   */
  not_found: 'fatal',
  rate_limited: 'retry',
  quota_exceeded: 'fatal',
  session_expired: 'retry',
  audio_unsupported: 'fatal',
  audio_overflow: 'retry',
  model_unavailable: 'retry',
  model_error: 'retry',
  going_away: 'retry',
  internal: 'retry',
}

export function recoveryFor(code: ErrorCode): Recovery {
  return RECOVERY[code]
}

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value)
}

/**
 * WebSocket close codes.
 *
 * 4000 upward is the range reserved for applications. The standard codes are
 * deliberately not reused: a browser generates 1006 on its own whenever a
 * connection drops without a close frame, so a server that also sends 1006
 * makes "the network died" and "the server rejected you" indistinguishable in
 * exactly the situation where telling them apart matters most.
 */
export const CLOSE_CODES = {
  /** The conversation ended the way it was meant to. */
  normal: 1000,
  /** The client is wrong and reconnecting will not help. */
  policy: 4000,
  /** Credentials missing, bad or expired. Get new ones, then reconnect. */
  unauthorized: 4001,
  /** Slow down. */
  rateLimited: 4002,
  /** The session ran past its ceiling. */
  expired: 4003,
  /** The server is going away. Reconnect after a short, jittered wait. */
  goingAway: 4004,
  /** The server broke. */
  internal: 4005,
} as const

export type CloseCode = (typeof CLOSE_CODES)[keyof typeof CLOSE_CODES]

/** The close code that fits an error, for the last frame before hanging up. */
export function closeCodeFor(code: ErrorCode): CloseCode {
  switch (code) {
    case 'unauthorized':
    case 'token_expired':
    case 'revoked':
      return CLOSE_CODES.unauthorized
    /*
     * Written out rather than left to the default, because `unauthorized` is
     * the tempting wrong answer. 4001 means "get new credentials, then
     * reconnect", and a client that hears that after being refused for its
     * role reconnects with a new token, is refused again, and loops.
     */
    case 'forbidden':
      return CLOSE_CODES.policy
    case 'rate_limited':
    case 'audio_overflow':
      return CLOSE_CODES.rateLimited
    case 'session_expired':
    case 'quota_exceeded':
      return CLOSE_CODES.expired
    case 'going_away':
      return CLOSE_CODES.goingAway
    case 'internal':
    case 'model_error':
    case 'model_unavailable':
      return CLOSE_CODES.internal
    default:
      return CLOSE_CODES.policy
  }
}
